<template>
  <div
    class="fixed inset-0 z-50 overflow-y-auto"
    role="dialog"
    aria-modal="true"
    aria-labelledby="modal-title"
  >
    <!-- Background overlay -->
    <div
      class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0"
    >
      <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" />

      <!-- This element is to trick the browser into centering the modal contents. -->
      <span
        class="hidden sm:inline-block sm:align-middle sm:h-screen"
        aria-hidden="true"
      >&#8203;</span>

      <!-- Modal panel -->
      <div
        class="relative inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6"
      >
        <div class="sm:flex sm:items-start">
          <div
            class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-warning-100 dark:bg-warning-900/50 sm:mx-0 sm:h-10 sm:w-10"
          >
            <svg
              class="h-6 w-6 text-warning-600 dark:text-warning-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
            <h3
              id="modal-title"
              class="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100"
            >
              Session Expiring Soon
            </h3>
            <div class="mt-2">
              <p class="text-sm text-gray-500 dark:text-gray-400">
                Your session will expire in {{ formattedTimeRemaining }}. Would you like to extend
                your session?
              </p>
            </div>
          </div>
        </div>

        <!-- Countdown progress bar -->
        <div class="mt-4">
          <div
            class="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-1"
          >
            <span>Time remaining</span>
            <span>{{ Math.floor(timeRemaining / 60) }}:{{
              String(timeRemaining % 60).padStart(2, '0')
            }}</span>
          </div>
          <div class="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
            <div
              class="bg-warning-500 h-2 rounded-full transition-all duration-1000 ease-linear"
              :style="{ width: `${progressPercentage}%` }"
            />
          </div>
        </div>

        <!-- Action buttons -->
        <div class="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
          <button
            type="button"
            class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
            @click="handleExtend"
          >
            Extend Session
          </button>
          <button
            type="button"
            class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-700 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm"
            @click="handleLogout"
          >
            Sign Out
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';

const emit = defineEmits<{
  extend: [];
  logout: [];
}>();
const timeRemaining = ref(300); // 5 minutes in seconds
const initialTime = 300;

// Computed properties
const formattedTimeRemaining = computed(() => {
  const minutes = Math.floor(timeRemaining.value / 60);
  const seconds = timeRemaining.value % 60;

  if (minutes > 0) {
    return `${minutes} minute${minutes !== 1 ? 's' : ''} and ${seconds} second${seconds !== 1 ? 's' : ''}`;
  } else {
    return `${seconds} second${seconds !== 1 ? 's' : ''}`;
  }
});

const progressPercentage = computed(() => {
  return (timeRemaining.value / initialTime) * 100;
});

// Timer functionality
let countdownInterval: number | null = null;

const startCountdown = () => {
  countdownInterval = window.setInterval(() => {
    if (timeRemaining.value > 0) {
      timeRemaining.value--;
    } else {
      // Auto logout when time reaches 0
      handleLogout();
    }
  }, 1000);
};

const stopCountdown = () => {
  if (countdownInterval) {
    clearInterval(countdownInterval);
    countdownInterval = null;
  }
};

// Event handlers
const handleExtend = () => {
  stopCountdown();
  emit('extend');
};

const handleLogout = () => {
  stopCountdown();
  emit('logout');
};

// Lifecycle
onMounted(() => {
  startCountdown();

  // Focus on extend button for accessibility
  const extendButton = document.querySelector('[data-extend-button]') as HTMLElement;
  if (extendButton) {
    extendButton.focus();
  }
});

onUnmounted(() => {
  stopCountdown();
});

// Handle escape key
const handleKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Escape') {
    handleLogout();
  }
};

onMounted(() => {
  document.addEventListener('keydown', handleKeydown);
});

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown);
});
</script>

<style scoped>
/* Ensure modal appears above other content */
.fixed.inset-0.z-50 {
  z-index: 9999;
}

/* Smooth animations */
.transition-opacity {
  transition: opacity 150ms ease-in-out;
}

.transform.transition-all {
  transition: all 150ms ease-in-out;
}

/* Accessibility improvements */
@media (prefers-reduced-motion: reduce) {
  .transition-opacity,
  .transform.transition-all,
  .transition-all {
    transition: none;
  }
}

/* High contrast mode */
@media (prefers-contrast: high) {
  .bg-warning-100 {
    @apply border-2 border-warning-600;
  }

  .dark .bg-warning-900\/50 {
    @apply border-2 border-warning-400;
  }
}
</style>
